import { Experience } from '@ninetailed/experience.js-next';
import config from 'config';
import { IEntry } from 'global';
import React, { useMemo } from 'react';
import { parseExperiences } from 'utils/ninetailed';
import { omitNullableHandler } from 'utils/type-helper';
import { capitalize } from '../../../../utils/strings';
import Sections from '../../sections';
import Image from '../../sections/components/image';
import Slider from '../../sections/slider';
import { useContentful } from '../context';

type ContentCmsHeaderContentStaticProps = {
  /** next image priority to be passed down to slider component  */
  priority?: boolean;
  /** if true slider design is override  */
  overrideSliderDesign?: boolean;
};

/**
 * Renders header field in product page.
 * For the purpose of rendering Section field, use ContentCmsContentStatic
 * @see : src/modules/content-cms/components/content/index.tsx
 */
const ContentCmsHeaderContentStatic: React.FC<
  React.PropsWithChildren<ContentCmsHeaderContentStaticProps>
> = ({ children, priority, overrideSliderDesign = false }) => {
  const { nodeEnv } = config;
  const { data: contentful } = useContentful();

  const headerData: Array<IEntry> = useMemo(() => {
    return (
      (contentful.fields.header as Array<IEntry>)?.filter(
        omitNullableHandler
      ) || []
    );
  }, [contentful]);

  /**
   * For Header we need to filter entries with contentType "usp" and "slide" from header array
   * and display them seperately for backwards compatibility
   * Because "slide" and "usp" could be injected directly without it being wrapped with slider and
   * Rest of the sections can be displayed normally
   */
  const slides: Array<any> = useMemo(() => {
    return (
      headerData.filter(
        (headerEntry) => headerEntry.sys.contentType.sys.id === 'slide'
      ) || []
    );
  }, [headerData]);
  const usps: Array<any> = useMemo(() => {
    return (
      headerData.filter(
        (headerEntry) => headerEntry.sys.contentType.sys.id === 'usp'
      ) || []
    );
  }, [headerData]);

  if (headerData.length === 0 && children) return <>{children}</>;

  return (
    <>
      {slides.length > 0 && (
        <Slider priority={priority} slides={slides} autoplay={false} />
      )}
      {usps.length > 0 && (
        <div className="-mx-2 flex flex-wrap justify-between pt-4 lg:flex-nowrap">
          {usps.map((usp) => (
            <div
              key={usp.sys.id}
              className="flex w-1/2 flex-col items-center px-2 pt-3 lg:w-full lg:max-w-xs lg:flex-row lg:pt-0"
            >
              {usp.fields.icon && (
                <div className="mx-auto mb-2 w-10 text-primary-main lg:mx-0 lg:mb-0 lg:w-12">
                  <Image {...usp.fields.icon} className="h-auto" />
                </div>
              )}
              <p className="text-center text-sm leading-tight text-text-light-bg-secondary lg:pl-4 lg:text-left lg:text-base">
                {usp.fields.text && usp.fields.text}
              </p>
            </div>
          ))}
        </div>
      )}

      {headerData.map((section) => {
        const componentIdentifier = capitalize(section.sys.contentType.sys.id);

        // Don't show slides or usps again
        if (
          section.sys.contentType.sys.id === 'usp' ||
          section.sys.contentType.sys.id === 'slide'
        ) {
          return '';
        }

        if ('productUsp' in section.fields && section.fields.productUsp)
          return '';

        if (typeof Sections[componentIdentifier] === 'undefined') {
          if (nodeEnv === 'development') {
            return (
              <div
                key={section.sys.id}
                className="bg-red-500 p-5 text-center font-black text-text-dark-bg-contrast-white"
              >
                Template missing for {componentIdentifier}
              </div>
            );
          }

          console.warn('T-Miss:', componentIdentifier);
          return '';
        }

        const Component = Sections[componentIdentifier];

        // TO DO: This condition has been added becaause the ExperienceWrapper cause flickering with some components
        // Need to find a better solution to fix this.
        // @see: https://petsdeli.atlassian.net/browse/PT-5596
        if (typeof section.fields['nt_experiences'] !== 'undefined') {
          return (
            <Experience
              key={section.sys.id}
              {...section.fields}
              id={section.sys.id}
              experiences={parseExperiences({
                entry: section,
              })}
              component={Component as any}
              passthroughProps={{ overrideSliderDesign, priority }}
            />
          );
        } else {
          return (
            <Component
              key={section.sys.id}
              {...(section.fields as any)}
              overrideSliderDesign={overrideSliderDesign}
              priority={priority}
            />
          );
        }
      })}
    </>
  );
};

export default ContentCmsHeaderContentStatic;
